import React, {PureComponent} from 'react'
import {connect} from 'react-redux'

import clamp from 'embo/utils/math/clamp'

// actions
import {
  setPlaylistVisible,
  setPlaying,
  setVolume,
  setCurrentTime, storeCurrentTime,
  setCurrentDuration,
} from 'embo/state/player'
import {
  nextTrack, previousTrack, setCurrentTrack,
  addTrack, removeTrack, moveTrack,
  setInfoPanelTrack, nextTrackInfo, previousTrackInfo,
  audioError, audioLoadSuccess,
  trackPresented,
  // selectors
  selectTracks,
  selectCurrentTrack,
  selectInfoPanelTrack,
} from 'embo/state/playlist'

import Audio from './Audio'
import PlayButton from './PlayButton'
import NextButton from './NextButton'
import PrevButton from './PrevButton'
import PlaylistButton from './PlaylistButton'
import TitleBar from './TitleBar'
import TrackInfoButton from './TrackInfoButton'
import Progress from './Progress'
import Timer from './Timer'
import Volume from './Volume'
import Playlist from './Playlist'
import TrackInfoPanel from './TrackInfoPanel'


const {floor} = Math


const mapStateToProps = state => {
  const {currentIndex, currentTrackId, presentedTrackId} = state.playlist
  const {
    playlistVisible, infoPanelVisible,
    playing, currentTime, requestedCurrentTime, currentDuration,
    volume,
  } = state.player
  return {
    tracks: selectTracks(state),
    currentIndex,
    currentTrackId,
    currentTrack: selectCurrentTrack(state),
    presentedTrackId,
    playlistVisible,
    infoPanelVisible,
    infoPanelTrack: selectInfoPanelTrack(state),
    playing,
    currentTime,
    requestedCurrentTime,
    currentDuration,
    volume,
  }
}

class Player extends PureComponent {
  constructor() {
    super()
    this._chainTimeout = 0
  }

  componentWillUnmount() {
    clearTimeout(this._chainTimeout)
  }

  _chainPlay() {
    this._chainTimeout = setTimeout(() => this._setPlaying(true), 2000)
  }

  _setPlaying(playing) {
    clearTimeout(this._chainTimeout)
    this.props.dispatch(setPlaying(playing))
  }

  render() {
    const {
      dispatch,
      playing,
      currentTime, requestedCurrentTime,
      currentDuration,
      volume,
      tracks,
      currentIndex,
      currentTrackId,
      presentedTrackId,
      currentTrack,
      playlistVisible,
      infoPanelVisible,
      infoPanelTrack,
    } = this.props

    const progress = currentDuration ? clamp(currentTime / currentDuration, 0, 1) : 0

    return (
      <div className="embo-player">

        <Audio
          src={currentTrack ? currentTrack.url : null}
          playing={playing} volume={volume} currentTime={requestedCurrentTime}
          onPlay={this.handleAudioPlay}
          onPause={this.handleAudioPause}
          onLoad={this.handleAudioLoad}
          onEnd={this.handleAudioEnd}
          onError={this.handleAudioError}
          onVolume={this.handleAudioVolumeChange}
          onTimer={this.handleAudioTimeChange}
          onDuration={this.handleAudioDurationChange}
        />

        <div className="embo-player__controls">
          <div className="embo-player__controls__action">
            <PlaylistButton
              open={playlistVisible}
              onClick={this.handlePlaylistToggle}
            />
            <PrevButton onClick={this.handlePrevClicked}/>
            <PlayButton playing={playing} onClick={this.handlePlayClicked}/>
            <NextButton onClick={this.handleNextClicked} />
            {currentTrack && (
              <TrackInfoButton onClick={() => this.handleTrackInfoToggle(currentTrack.id)}/>
            )}
          </div>
          <div className="embo-player__statusbar" style={{'--progress': progress}}>
            <TitleBar track={currentTrack}/>
            <Progress
                position={currentTime}
                duration={currentDuration}
                onSeek={this.handleProgressbarSeek}
              />
          </div>
          <div className="embo-player__timers">
            <Timer value={floor(currentTime)} title="Position"/>
            /
            <Timer value={floor(currentDuration)} title="Duration"/>
          </div>
          <Volume volume={volume} onChange={volume => dispatch(setVolume(volume))}/>
        </div>

        <Playlist
          visible={playlistVisible}
          tracks={tracks} current={currentTrackId} presented={presentedTrackId}
          onItemSelected={this.handleTrackSelected}
          onItemRemoved={this.handleTrackRemoved}
          onItemMoved={this.handleTrackMoved}
          onTrackInfoRequest={this.handleTrackInfoRequest}
          onTrackPresented={this.handleTrackPresented}
        />

        <TrackInfoPanel
          visible={infoPanelVisible}
          track={infoPanelTrack}
          onNext={this.handleInfoPanelNext}
          onPrevious={this.handleInfoPanelPrev}
          onClose={this.handleInfoPanelClose}
        />

      </div>
    )
  }

  handleAudioPlay = () => this._setPlaying(true)
  handleAudioPause = () => this._setPlaying(false)

  handleAudioLoad = () => this.props.dispatch(audioLoadSuccess(this.props.currentTrackId))
  handleAudioError = err => this.props.dispatch(audioError(this.props.currentTrackId, err))

  handleAudioEnd = () => {
    this.props.dispatch(nextTrack())
    this._chainPlay()
  }

  handleAudioVolumeChange = volume => this.props.dispatch(setVolume(volume))
  handleAudioTimeChange = currentTime => this.props.dispatch(storeCurrentTime(currentTime))
  handleAudioDurationChange = duration => this.props.dispatch(setCurrentDuration(duration))

  handlePlayClicked = () => this._setPlaying(!this.props.playing)

  handleNextClicked = () => {
    this.props.dispatch(nextTrack())
    this._setPlaying(true)
  }

  handlePrevClicked = () => {
    this.props.dispatch(previousTrack())
    this._setPlaying(true)
  }

  handleTrackSelected = trackId => {
    this.props.dispatch(setCurrentTrack(trackId))
    this._setPlaying(true)
  }

  handleProgressbarSeek = position => this.props.dispatch(setCurrentTime(position))

  handleTrackRemoved = trackId => {
    if (trackId === this.props.currentTrackId) {
      this.props.dispatch(storeCurrentTime(0))
      this.props.dispatch(setCurrentDuration(0))
    }
    this.props.dispatch(removeTrack(trackId))
  }

  handleTrackMoved = (trackId, position) => this.props.dispatch(moveTrack(trackId, position))

  handleTrackInfoRequest = trackId => {
    if (this.props.playlistVisible) {
      this.props.dispatch(setPlaylistVisible(false))
    }
    this.props.dispatch(setInfoPanelTrack(trackId))
  }

  handleTrackInfoToggle = trackId => {
    if (this.props.infoPanelTrack) {
      this.handleInfoPanelClose()
    } else {
      this.handleTrackInfoRequest(trackId)
    }
  }

  handlePlaylistToggle = () => {
    if (this.props.infoPanelTrack) {
      this.handleInfoPanelClose()
    }
    this.props.dispatch(setPlaylistVisible(!this.props.playlistVisible))
  }

  handleTrackPresented = () => this.props.dispatch(trackPresented())

  handleInfoPanelNext = () => this.props.dispatch(nextTrackInfo())
  handleInfoPanelPrev = () => this.props.dispatch(previousTrackInfo())
  handleInfoPanelClose = () => this.props.dispatch(setInfoPanelTrack(null))

  /**
   * TODO: handle dragging new tracks to playlist
   */
  handleTrackDrop = (track, pos) => this.props.dispatch(addTrack(track, pos))
}

export default connect(mapStateToProps)(Player)
